import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import {
	Stack,
	Title,
	TextInput,
	Box,
	Group,
	Button,
	SegmentedControl,
	Card,
	Text,
	Anchor,
	Badge,
	SimpleGrid,
	Stepper,
	Table,
	Checkbox,
	Loader,
	Pagination,
	FileInput,
	ColorInput,
	Tabs,
	Menu,
} from '@mantine/core'
import {
	createBlast,
	getBlast,
	sendBlast,
	sendBlastTest,
	updateBlast,
} from '../services/actions/messagingActions'
import {
	getMessageSetups,
	getStationOptions,
	getItemOptions,
	getExperience,
} from '../services/actions/experienceActions'
import { getExperienceCheckIns } from '../services/actions/checkInActions'
import {
	STOCK_TEMPLATES,
	EMAIL_SEPARATOR_TOKEN,
	DEFAULT_SEPARATOR_COLOR,
	appendPassLinkToSmsBody,
	buildStandardEmailHtml,
	findSetupForTemplate,
	getStockTemplate,
	getTemplateEditorDefaults,
	isTemplateConfigured,
	isCheckInPassTemplate,
	normalizeDeliveryMethod,
	renderEmailBodyHtml,
	buildVariableOptions,
	buildPassPreviewUrl,
} from '../constants/stockTemplates'
import {
	uploadImageToS3,
	uploadMessagingMediaToS3,
} from '../services/utils/uploadImageToS3'
import ImageCropperModal from '../components/ImageCropperModal'
import BlastSendModal from '../components/BlastSendModal'
import { readFileAsDataUrl } from '../services/utils/imageCropper'
import VariableTriggerTextarea from '../components/VariableTriggerTextarea'
import CheckInFilterBlock, {
	ADDITIONAL_FILTER_LIMIT,
	getCheckInCustomFields,
	matchesAdditionalCheckInFilters,
} from '../components/CheckInFilterBlock'
import {
	getBlastScheduleError,
	toUtcIsoInTimezone,
} from '../services/utils/blastScheduling'

const ON_DEMAND_TEMPLATES = STOCK_TEMPLATES.filter(
	(template) => template.type === 'on_demand',
)
const SCRATCH_EVENT_VARIABLES = [
	'{{travelerName}}',
	'{{groupLeaderName}}',
	'{{groupLeaderPhone}}',
	'{{experienceName}}',
	'{{orgName}}',
]
const HEADER_IMAGE_TARGET_WIDTH = 1200
const HEADER_IMAGE_TARGET_HEIGHT = 420

const deliveryToChannel = (deliveryMethod) => {
	const normalized = normalizeDeliveryMethod(deliveryMethod)
	if (normalized === 'sms') return 'SMS'
	if (normalized === 'both') return 'BOTH'
	return 'EMAIL'
}

const replaceAll = (value, needle, replacement) =>
	String(value || '')
		.split(needle)
		.join(replacement)

const getNonEmptyTrimmedValue = (...values) => {
	for (const value of values) {
		if (typeof value !== 'string') continue
		const normalized = value.trim()
		if (normalized) return normalized
	}
	return null
}

const applyMessageVariables = ({
	value = '',
	travelerName = '',
	groupLeaderName = '',
	groupLeaderPhone = '',
	groupMemberNames = '',
	experienceName = '',
	orgName = '',
	clickHereToViewPass = '',
}) => {
	let output = String(value || '')
	output = replaceAll(output, '{{travelerName}}', travelerName || '')
	output = replaceAll(output, '{{groupLeaderName}}', groupLeaderName || '')
	output = replaceAll(output, '{{groupLeaderPhone}}', groupLeaderPhone || '')
	output = replaceAll(output, '{{groupMemberNames}}', groupMemberNames || '')
	output = replaceAll(output, '{{experienceName}}', experienceName || '')
	output = replaceAll(output, '{{orgName}}', orgName || '')
	output = replaceAll(
		output,
		'{{clickHereToViewPass}}',
		clickHereToViewPass || '',
	)
	return output
}

const getBrowserTimezone = () =>
	Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'

const toDateTimeLocalInTimezone = (isoValue, timezone) => {
	if (!isoValue) return ''
	const date = new Date(isoValue)
	if (!Number.isFinite(date.getTime())) return ''
	const formatter = new Intl.DateTimeFormat('en-CA', {
		timeZone: timezone,
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
		hour: '2-digit',
		minute: '2-digit',
		hourCycle: 'h23',
	})
	const parts = formatter
		.formatToParts(date)
		.reduce((acc, part) => ({ ...acc, [part.type]: part.value }), {})
	return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`
}

const buildBodyDescription = (channel, smsBody, smsMediaUrl = '') =>
	channel === 'SMS' || channel === 'BOTH'
		? smsMediaUrl
			? 'MMS with image'
			: `${Math.ceil((smsBody.length || 0) / 160)} SMS segment(s)`
		: ''

export default function BlastComposerPage() {
	const { orgSlug, experienceSlug, id } = useParams()
	const navigate = useNavigate()
	const isEditingBlast = !!id
	const experience = useSelector((state) => state.experience?.experienceDetails)
	const currentExperience = useSelector(
		(state) => state.user?.currentExperience,
	)
	const user = useSelector((state) => state.user?.authUser)
	const currentOrg = useSelector((state) => state.user?.currentOrg)
	const [previewExperience, setPreviewExperience] = useState(null)
	const useExperiencePage =
		previewExperience?.useExperiencePage ??
		experience?.useExperiencePage ??
		currentExperience?.useExperiencePage ??
		true
	const previewExperienceId =
		previewExperience?.id || experience?.id || currentExperience?.id || ''
	const resolvedExperienceName =
		getNonEmptyTrimmedValue(
			experience?.shortName,
			currentExperience?.shortName,
			experience?.name,
			currentExperience?.name,
			experienceSlug,
		) || 'Experience'

	const [activeStep, setActiveStep] = useState(0)
	const [source, setSource] = useState('scratch')
	const [templateKey, setTemplateKey] = useState(null)
	const [channel, setChannel] = useState('EMAIL')
	const [emailSubject, setEmailSubject] = useState('')
	const [emailBody, setEmailBody] = useState('')
	const [smsBody, setSmsBody] = useState('')
	const [smsMediaUrl, setSmsMediaUrl] = useState('')
	const [appendPassLinkToSms, setAppendPassLinkToSms] = useState(false)
	const [clickText, setClickText] = useState('CLICK HERE')
	const [headerImageUrl, setHeaderImageUrl] = useState('')
	const [headerText, setHeaderText] = useState('')
	const [separatorColor, setSeparatorColor] = useState(DEFAULT_SEPARATOR_COLOR)
	const [activeEditorTab, setActiveEditorTab] = useState('email')
	const [cropModalOpened, setCropModalOpened] = useState(false)
	const [cropSourceUrl, setCropSourceUrl] = useState('')
	const [uploadingHeaderImage, setUploadingHeaderImage] = useState(false)
	const [uploadingSmsMedia, setUploadingSmsMedia] = useState(false)

	const [messageSetups, setMessageSetups] = useState([])
	const [loadingTemplates, setLoadingTemplates] = useState(true)

	const [checkIns, setCheckIns] = useState([])
	const [stationOptions, setStationOptions] = useState([])
	const [itemOptions, setItemOptions] = useState([])
	const [loadingRecipients, setLoadingRecipients] = useState(false)
	const [recipientsLoaded, setRecipientsLoaded] = useState(false)

	const [selectedCheckInIds, setSelectedCheckInIds] = useState([])
	const [searchTerm, setSearchTerm] = useState('')
	const [stationFilter, setStationFilter] = useState([])
	const [itemFilter, setItemFilter] = useState([])
	const [checkInStatusFilter, setCheckInStatusFilter] = useState('all')
	const [customFieldFilters, setCustomFieldFilters] = useState([])
	const [recipientsPage, setRecipientsPage] = useState(1)

	const [sendModalMode, setSendModalMode] = useState(null)
	const [scheduleLocalValue, setScheduleLocalValue] = useState('')
	const [scheduleError, setScheduleError] = useState('')
	const [loadingBlast, setLoadingBlast] = useState(isEditingBlast)
	const [editingScheduledBlast, setEditingScheduledBlast] = useState(false)
	const [submitting, setSubmitting] = useState(false)
	const [sendingTest, setSendingTest] = useState(false)
	const [error, setError] = useState('')
	const [testSendMessage, setTestSendMessage] = useState('')

	const scheduledTimezone = experience?.timezone || getBrowserTimezone()
	const selectedTemplate = useMemo(
		() => getStockTemplate(templateKey),
		[templateKey],
	)
	const isScratchSource = source === 'scratch'
	const isTemplateSource = source === 'template' && !!selectedTemplate
	const hasClickText = !!selectedTemplate?.hasClickText
	const selectedTemplateIsCheckInPass = isCheckInPassTemplate(selectedTemplate)
	const isEmailEnabled = channel !== 'SMS'
	const isSmsEnabled = channel === 'SMS' || channel === 'BOTH'
	const showTabbedEditors = isScratchSource && channel === 'BOTH'
	const availableVariables = useMemo(() => {
		if (selectedTemplate?.variables?.length) return selectedTemplate.variables
		return SCRATCH_EVENT_VARIABLES
	}, [selectedTemplate])
	const availableVariableOptions = useMemo(
		() => buildVariableOptions(availableVariables, { useExperiencePage }),
		[availableVariables, useExperiencePage],
	)
	const previewContext = useMemo(
		() => ({
			to: `${user?.name || 'Demo User'} <${user?.email || 'traveler@example.com'}>`,
			smsTo: user?.phone || 'No phone number on your user profile',
			travelerName: 'John Doe',
			groupLeaderName: 'Jane Doe',
			groupLeaderPhone: '+1 (555) 123-4567',
			groupMemberNames: 'Jeff Gordon, Richard Petty, Chase Elliott',
			experienceName: resolvedExperienceName,
			orgName: currentOrg?.orgName || 'LiveHQ',
			clickHereToViewPass: buildPassPreviewUrl(
				useExperiencePage,
				previewExperienceId,
			),
		}),
		[
			user,
			resolvedExperienceName,
			currentOrg,
			useExperiencePage,
			previewExperienceId,
		],
	)

	useEffect(() => {
		if (channel === 'SMS') {
			setActiveEditorTab('sms')
			return
		}
		setActiveEditorTab('email')
	}, [channel])

	useEffect(() => {
		;(async () => {
			try {
				setLoadingTemplates(true)
				const [setups, latestExperience] = await Promise.all([
					getMessageSetups(experienceSlug, orgSlug),
					getExperience(experienceSlug, orgSlug),
				])
				setMessageSetups(setups)
				setPreviewExperience(latestExperience)
			} catch (err) {
				console.error('Failed to load template setups', err)
				setError(err.message || 'Failed to load template setups.')
			} finally {
				setLoadingTemplates(false)
			}
		})()
	}, [experienceSlug, orgSlug])

	useEffect(() => {
		if (!isEditingBlast || !id) {
			setLoadingBlast(false)
			setEditingScheduledBlast(false)
			return
		}

		let cancelled = false
		;(async () => {
			try {
				setLoadingBlast(true)
				setError('')
				const blast = await getBlast(orgSlug, experienceSlug, id)
				if (cancelled) return

				const scheduledAtMs = blast?.scheduledAt
					? new Date(blast.scheduledAt).getTime()
					: null
				const isFutureScheduled =
					Number.isFinite(scheduledAtMs) && scheduledAtMs > Date.now()
				const canEditBlast =
					blast?.status === 'DRAFT' ||
					(blast?.status === 'QUEUED' && isFutureScheduled)

				if (!canEditBlast) {
					setError(
						'Only draft blasts or scheduled future blasts can be edited.',
					)
					navigate(
						`/${orgSlug}/experiences/${experienceSlug}/messaging/blasts/${id}`,
					)
					return
				}

				const audienceFilter =
					blast?.audienceFilter &&
					typeof blast.audienceFilter === 'object' &&
					!Array.isArray(blast.audienceFilter)
						? blast.audienceFilter
						: {}
				const content =
					audienceFilter?.content &&
					typeof audienceFilter.content === 'object' &&
					!Array.isArray(audienceFilter.content)
						? audienceFilter.content
						: {}
				const filters =
					audienceFilter?.filters &&
					typeof audienceFilter.filters === 'object' &&
					!Array.isArray(audienceFilter.filters)
						? audienceFilter.filters
						: {}
				const sourceValue =
					audienceFilter?.source === 'template' ? 'template' : 'scratch'
				const templateValue =
					sourceValue === 'template' &&
					typeof audienceFilter?.templateKey === 'string'
						? audienceFilter.templateKey
						: null
				const loadedTemplate = getStockTemplate(templateValue)

				setRecipientsLoaded(false)
				setCheckIns([])
				setSource(sourceValue)
				setTemplateKey(templateValue)
				setChannel(blast?.channel || 'EMAIL')
				setEmailSubject(
					typeof content.emailSubject === 'string'
						? content.emailSubject
						: blast?.subject || '',
				)
				setEmailBody(
					typeof content.emailBodyText === 'string'
						? content.emailBodyText
						: blast?.channel !== 'SMS'
							? blast?.bodyText || ''
							: '',
				)
				setSmsBody(
					typeof content.smsBodyText === 'string'
						? content.smsBodyText
						: blast?.channel !== 'EMAIL'
							? blast?.bodyText || ''
							: '',
				)
				setSmsMediaUrl(
					typeof content.smsMediaUrl === 'string'
						? content.smsMediaUrl
						: typeof content.mmsMediaUrl === 'string'
							? content.mmsMediaUrl
							: '',
				)
				setAppendPassLinkToSms(
					isCheckInPassTemplate(loadedTemplate)
						? content.appendPassLinkToSms !== false
						: content.appendPassLinkToSms === true,
				)
				setClickText(
					typeof content.clickText === 'string' && content.clickText.trim()
						? content.clickText
						: 'CLICK HERE',
				)
				setHeaderImageUrl(
					typeof content.headerImageUrl === 'string'
						? content.headerImageUrl
						: '',
				)
				setHeaderText(
					typeof content.headerText === 'string' ? content.headerText : '',
				)
				setSeparatorColor(
					typeof content.separatorColor === 'string' &&
						content.separatorColor.trim()
						? content.separatorColor
						: DEFAULT_SEPARATOR_COLOR,
				)
				setSelectedCheckInIds(
					Array.isArray(audienceFilter?.selectedCheckInIds)
						? audienceFilter.selectedCheckInIds
						: [],
				)
				setStationFilter(
					Array.isArray(filters?.stationIds)
						? filters.stationIds.filter(Boolean)
						: [],
				)
				setItemFilter(
					Array.isArray(filters?.itemIds)
						? filters.itemIds.filter(Boolean)
						: [],
				)
				setCheckInStatusFilter(
					filters?.checkInStatus === 'checkedIn' ||
						filters?.checkInStatus === 'notCheckedIn'
						? filters.checkInStatus
						: 'all',
				)
				setSearchTerm(typeof filters?.search === 'string' ? filters.search : '')
				setCustomFieldFilters(
					Array.isArray(filters?.customFieldFilters)
						? filters.customFieldFilters
								.slice(0, ADDITIONAL_FILTER_LIMIT)
								.map((filter, index) => ({
									id: filter?.id || `loaded-${index}`,
									field:
										typeof filter?.field === 'string' ? filter.field : '',
									values: Array.isArray(filter?.values)
										? filter.values.filter(Boolean)
										: [],
								}))
						: [],
				)
				setScheduleLocalValue(
					isFutureScheduled
						? toDateTimeLocalInTimezone(blast?.scheduledAt, scheduledTimezone)
						: '',
				)
				setEditingScheduledBlast(
					blast?.status === 'QUEUED' && isFutureScheduled,
				)
				setActiveStep(1)
			} catch (err) {
				if (!cancelled) {
					setError(err.message || 'Failed to load blast for editing.')
				}
			} finally {
				if (!cancelled) {
					setLoadingBlast(false)
				}
			}
		})()

		return () => {
			cancelled = true
		}
	}, [isEditingBlast, id, orgSlug, experienceSlug, navigate, scheduledTimezone])

	useEffect(() => {
		if (activeStep !== 2 || recipientsLoaded) return
		;(async () => {
			try {
				setLoadingRecipients(true)
				const [checkInData, stations, items] = await Promise.all([
					getExperienceCheckIns(experienceSlug, orgSlug),
					getStationOptions(experienceSlug, orgSlug),
					getItemOptions(experienceSlug, orgSlug),
				])
				setCheckIns(Array.isArray(checkInData) ? checkInData : [])
				setStationOptions(Array.isArray(stations) ? stations : [])
				setItemOptions(Array.isArray(items) ? items : [])
				setRecipientsLoaded(true)
			} catch (err) {
				console.error('Failed to load recipient options', err)
				setError(err.message || 'Failed to load recipients.')
			} finally {
				setLoadingRecipients(false)
			}
		})()
	}, [activeStep, recipientsLoaded, experienceSlug, orgSlug])

	const templateSetupMap = useMemo(
		() =>
			Object.fromEntries(
				ON_DEMAND_TEMPLATES.map((template) => [
					template.key,
					findSetupForTemplate(messageSetups, template),
				]),
			),
		[messageSetups],
	)
	const availableOnDemandTemplates = useMemo(() => {
		return ON_DEMAND_TEMPLATES.filter((template) => {
			const setup = templateSetupMap[template.key]
			const templateChannel = deliveryToChannel(
				setup?.deliveryMethod || template.deliveryMethod,
			)
			if (channel === 'EMAIL') {
				return templateChannel === 'EMAIL' || templateChannel === 'BOTH'
			}
			if (channel === 'SMS') {
				return templateChannel === 'SMS' || templateChannel === 'BOTH'
			}
			return templateChannel === 'BOTH'
		})
	}, [channel, templateSetupMap])

	const filteredCheckIns = useMemo(() => {
		return checkIns.filter((row) => {
			const term = searchTerm.trim().toLowerCase()
			const customFieldText = Object.values(getCheckInCustomFields(row)).join(' ')
			const searchOk =
				!term ||
				row.travelerName?.toLowerCase().includes(term) ||
				row.travelerEmail?.toLowerCase().includes(term) ||
				row.travelerPhone?.toLowerCase().includes(term) ||
				row.bookingNumber?.toLowerCase().includes(term) ||
				row.groupId?.toLowerCase().includes(term) ||
				customFieldText.toLowerCase().includes(term)
			const stationOk =
				stationFilter.length === 0 || stationFilter.includes(row.stationId)
			const itemOk =
				itemFilter.length === 0 ||
				(Array.isArray(row.purchasedItems) &&
					row.purchasedItems.some((item) => itemFilter.includes(item?.id)))
			const statusOk =
				checkInStatusFilter === 'all' ||
				(checkInStatusFilter === 'checkedIn' && !!row.checkInStatus) ||
				(checkInStatusFilter === 'notCheckedIn' && !row.checkInStatus)
			const additionalFiltersOk = matchesAdditionalCheckInFilters(
				row,
				customFieldFilters,
			)
			return searchOk && stationOk && itemOk && statusOk && additionalFiltersOk
		})
	}, [
		checkIns,
		searchTerm,
		stationFilter,
		itemFilter,
		checkInStatusFilter,
		customFieldFilters,
	])

	const selectedSet = useMemo(
		() => new Set(selectedCheckInIds),
		[selectedCheckInIds],
	)

	const selectedTravelers = useMemo(
		() => checkIns.filter((row) => selectedSet.has(row.id)),
		[checkIns, selectedSet],
	)

	const selectedTravelerCount = selectedTravelers.length
	const selectedContactCount = useMemo(() => {
		return selectedTravelers.reduce((count, row) => {
			if (channel === 'EMAIL') return count + (row.travelerEmail ? 1 : 0)
			if (channel === 'SMS') return count + (row.travelerPhone ? 1 : 0)
			return count + (row.travelerEmail ? 1 : 0) + (row.travelerPhone ? 1 : 0)
		}, 0)
	}, [selectedTravelers, channel])

	const recipientPageSize = 50
	const paginatedFilteredCheckIns = useMemo(() => {
		const startIndex = (recipientsPage - 1) * recipientPageSize
		return filteredCheckIns.slice(startIndex, startIndex + recipientPageSize)
	}, [filteredCheckIns, recipientsPage])
	const recipientTotalPages = Math.ceil(
		filteredCheckIns.length / recipientPageSize,
	)
	const totalRecipientCount = checkIns.length
	const filteredRecipientCount = filteredCheckIns.length
	const currentRecipientStart =
		filteredRecipientCount > 0
			? (recipientsPage - 1) * recipientPageSize + 1
			: 0
	const currentRecipientEnd =
		filteredRecipientCount > 0
			? Math.min(recipientsPage * recipientPageSize, filteredRecipientCount)
			: 0
	const hasActiveRecipientFilters =
		searchTerm.trim().length > 0 ||
		stationFilter.length > 0 ||
		itemFilter.length > 0 ||
		checkInStatusFilter !== 'all' ||
		customFieldFilters.some(
			(filter) => filter.field || filter.values.length > 0,
		)
	const recipientCountLabel = hasActiveRecipientFilters
		? filteredRecipientCount > 0
			? `Showing ${currentRecipientStart}-${currentRecipientEnd} of ${filteredRecipientCount} filtered check-ins (${totalRecipientCount} total)`
			: `Showing 0 filtered check-ins (${totalRecipientCount} total)`
		: totalRecipientCount > 0
			? `Showing ${currentRecipientStart}-${currentRecipientEnd} of ${totalRecipientCount} check-ins`
			: 'Showing 0 check-ins'

	const visibleIds = paginatedFilteredCheckIns.map((row) => row.id)
	const allVisibleSelected =
		visibleIds.length > 0 && visibleIds.every((id) => selectedSet.has(id))
	const filteredIds = filteredCheckIns.map((row) => row.id)
	const allFilteredSelected =
		filteredIds.length > 0 && filteredIds.every((id) => selectedSet.has(id))
	const shouldShowToggleAllFilteredLink =
		allVisibleSelected &&
		filteredCheckIns.length > paginatedFilteredCheckIns.length &&
		filteredCheckIns.length > 0
	const toggleAllFilteredLinkLabel = allFilteredSelected
		? `Deselect all ${filteredCheckIns.length} travelers that match current filter`
		: `Select all ${filteredCheckIns.length} travelers that match current filter`

	useEffect(() => {
		setRecipientsPage(1)
	}, [
		searchTerm,
		stationFilter,
		itemFilter,
		checkInStatusFilter,
		customFieldFilters,
	])

	useEffect(() => {
		if (recipientTotalPages > 0 && recipientsPage > recipientTotalPages) {
			setRecipientsPage(recipientTotalPages)
		}
	}, [recipientsPage, recipientTotalPages])

	const editorValid =
		(channel === 'EMAIL' && !!emailBody.trim()) ||
		(channel === 'SMS' && !!smsBody.trim()) ||
		(channel === 'BOTH' && !!emailBody.trim() && !!smsBody.trim())
	const newBlastSubjectRequired = !isEditingBlast
	const creationValid =
		editorValid && (!newBlastSubjectRequired || !!emailSubject.trim())

	const renderedSubject = useMemo(() => {
		return applyMessageVariables({
			value: emailSubject || selectedTemplate?.defaultSubject || '',
			travelerName: previewContext.travelerName,
			groupLeaderName: previewContext.groupLeaderName,
			groupLeaderPhone: previewContext.groupLeaderPhone,
			groupMemberNames: previewContext.groupMemberNames,
			experienceName: previewContext.experienceName,
			orgName: previewContext.orgName,
			clickHereToViewPass: previewContext.clickHereToViewPass,
		})
	}, [emailSubject, selectedTemplate, previewContext])

	const renderedHeaderText = useMemo(
		() =>
			applyMessageVariables({
				value: headerText,
				travelerName: previewContext.travelerName,
				groupLeaderName: previewContext.groupLeaderName,
				groupLeaderPhone: previewContext.groupLeaderPhone,
				groupMemberNames: previewContext.groupMemberNames,
				experienceName: previewContext.experienceName,
				orgName: previewContext.orgName,
				clickHereToViewPass: previewContext.clickHereToViewPass,
			}),
		[headerText, previewContext],
	)

	const renderedEmailBody = useMemo(
		() =>
			renderEmailBodyHtml({
				template: selectedTemplate || { hasClickText: false },
				emailBody,
				clickText,
				clickUrl: previewContext.clickHereToViewPass,
				travelerName: previewContext.travelerName,
				groupLeaderName: previewContext.groupLeaderName,
				groupLeaderPhone: previewContext.groupLeaderPhone,
				groupMemberNames: previewContext.groupMemberNames,
				experienceName: previewContext.experienceName,
				orgName: previewContext.orgName,
				separatorColor,
			}),
		[selectedTemplate, emailBody, clickText, previewContext, separatorColor],
	)

	const renderedEmailHtml = useMemo(
		() =>
			buildStandardEmailHtml({
				headerImageUrl,
				headerText: renderedHeaderText,
				bodyHtml: renderedEmailBody,
				separatorColor,
			}),
		[headerImageUrl, renderedHeaderText, renderedEmailBody, separatorColor],
	)

	const renderedSmsBody = useMemo(() => {
		const body = applyMessageVariables({
			value: smsBody,
			travelerName: previewContext.travelerName,
			groupLeaderName: previewContext.groupLeaderName,
			groupLeaderPhone: previewContext.groupLeaderPhone,
			groupMemberNames: previewContext.groupMemberNames,
			experienceName: previewContext.experienceName,
			orgName: previewContext.orgName,
			clickHereToViewPass: previewContext.clickHereToViewPass,
		})
		if (!selectedTemplateIsCheckInPass || !appendPassLinkToSms) return body
		return appendPassLinkToSmsBody(body, previewContext.clickHereToViewPass)
	}, [
		smsBody,
		previewContext,
		selectedTemplateIsCheckInPass,
		appendPassLinkToSms,
	])

	const applyTemplateContent = (nextTemplateKey) => {
		const template = getStockTemplate(nextTemplateKey)
		if (!template) return
		const existingSetup = templateSetupMap[nextTemplateKey] || null
		const defaults = getTemplateEditorDefaults(template, existingSetup)
		setChannel(
			deliveryToChannel(defaults.deliveryMethod || template.deliveryMethod),
		)
		setEmailSubject(defaults.emailSubject || template.defaultSubject || '')
		setEmailBody(defaults.emailBody || '')
		setSmsBody(defaults.smsBody || '')
		setSmsMediaUrl(defaults.smsMediaUrl || '')
		setAppendPassLinkToSms(defaults.appendPassLinkToSms)
		setClickText(defaults.clickText || 'CLICK HERE')
		setHeaderImageUrl(defaults.headerImageUrl || '')
		setHeaderText(defaults.headerText || '')
		setSeparatorColor(defaults.separatorColor || DEFAULT_SEPARATOR_COLOR)
	}

	const handleInsertSeparatorInEmail = () => {
		setEmailBody(
			(prev) => `${prev}${prev ? '\n' : ''}${EMAIL_SEPARATOR_TOKEN}\n`,
		)
	}

	const resetScratchComposer = () => {
		setEmailSubject('')
		setEmailBody('')
		setSmsBody('')
		setSmsMediaUrl('')
		setAppendPassLinkToSms(false)
		setClickText('CLICK HERE')
		setHeaderImageUrl('')
		setHeaderText('')
		setSeparatorColor(DEFAULT_SEPARATOR_COLOR)
	}

	const handleHeaderImageSelect = async (file) => {
		if (!file) return
		try {
			const sourceUrl = await readFileAsDataUrl(file)
			setCropSourceUrl(sourceUrl)
			setCropModalOpened(true)
		} catch (err) {
			console.error('Failed to load image for crop', err)
			setError(err.message || 'Failed to load image for crop.')
		}
	}

	const handleOpenHeaderImageRecrop = () => {
		if (!headerImageUrl) return
		setCropSourceUrl(headerImageUrl)
		setCropModalOpened(true)
	}

	const handleUploadCroppedHeaderImage = async (croppedBlob) => {
		if (!croppedBlob) return
		try {
			setUploadingHeaderImage(true)
			const croppedFile = new File(
				[croppedBlob],
				`blast-header-${Date.now()}.jpg`,
				{ type: 'image/jpeg' },
			)
			const uploadedUrl = await uploadImageToS3(croppedFile)
			setHeaderImageUrl(uploadedUrl)
			setCropModalOpened(false)
		} catch (err) {
			console.error('Error uploading cropped image', err)
			setError(err.message || 'Failed to upload header image.')
		} finally {
			setUploadingHeaderImage(false)
		}
	}

	const handleSmsMediaSelect = async (file) => {
		if (!file) return
		try {
			setUploadingSmsMedia(true)
			const uploadedUrl = await uploadMessagingMediaToS3(file, orgSlug)
			setSmsMediaUrl(uploadedUrl)
		} catch (err) {
			console.error('Error uploading MMS image', err)
			setError(err.message || 'Failed to upload MMS image.')
		} finally {
			setUploadingSmsMedia(false)
		}
	}

	const handleChooseScratch = () => {
		setSource('scratch')
		setTemplateKey(null)
		resetScratchComposer()
		setActiveStep(1)
	}

	const handleChooseTemplate = (nextTemplateKey) => {
		setSource('template')
		setTemplateKey(nextTemplateKey)
		applyTemplateContent(nextTemplateKey)
		setActiveStep(1)
	}

	const toggleCheckIn = (id) => {
		setSelectedCheckInIds((prev) =>
			prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
		)
	}

	const toggleAllVisible = () => {
		setSelectedCheckInIds((prev) => {
			const prevSet = new Set(prev)
			if (allVisibleSelected) {
				visibleIds.forEach((id) => prevSet.delete(id))
			} else {
				visibleIds.forEach((id) => prevSet.add(id))
			}
			return [...prevSet]
		})
	}

	const handleToggleAllFiltered = () => {
		setSelectedCheckInIds((prev) => {
			const selectedIds = new Set(prev)
			if (allFilteredSelected) {
				filteredIds.forEach((id) => selectedIds.delete(id))
			} else {
				filteredIds.forEach((id) => selectedIds.add(id))
			}
			return [...selectedIds]
		})
	}

	const buildPayload = () => ({
		channel,
		source,
		templateKey: source === 'template' ? templateKey : null,
		emailSubject,
		emailBodyText: channel === 'SMS' ? '' : emailBody,
		smsBodyText: channel === 'EMAIL' ? '' : smsBody,
		scheduledAt: null,
		audienceFilter: {
			selectedCheckInIds,
			filters: {
				search: searchTerm,
				stationIds: stationFilter,
				itemIds: itemFilter,
				checkInStatus: checkInStatusFilter,
				customFieldFilters: customFieldFilters
					.filter((filter) => filter.field)
					.map((filter) => ({
						id: filter.id,
						field: filter.field,
						values: filter.values,
					})),
			},
			counts: {
				travelers: selectedTravelerCount,
				contacts: selectedContactCount,
			},
			content: {
				headerImageUrl: channel === 'SMS' ? '' : headerImageUrl,
				headerText: channel === 'SMS' ? '' : headerText,
				separatorColor:
					channel === 'SMS' ? DEFAULT_SEPARATOR_COLOR : separatorColor,
				smsMediaUrl: channel === 'EMAIL' ? '' : smsMediaUrl,
				clickText: hasClickText ? clickText : 'CLICK HERE',
				...(selectedTemplateIsCheckInPass
					? { appendPassLinkToSms }
					: {}),
			},
		},
	})

	const upsertBlast = async () => {
		if (isEditingBlast && id) {
			return updateBlast(orgSlug, experienceSlug, id, buildPayload())
		}
		return createBlast(orgSlug, experienceSlug, buildPayload())
	}

	const handleSaveDraft = async () => {
		if (!creationValid) return
		setSubmitting(true)
		setError('')
		try {
			const blast = await upsertBlast()
			navigate(
				`/${orgSlug}/experiences/${experienceSlug}/messaging/blasts/${blast.id}`,
			)
		} catch (err) {
			setError(
				err.message ||
					(isEditingBlast
						? 'Failed to save blast changes.'
						: 'Failed to save draft.'),
			)
		} finally {
			setSubmitting(false)
		}
	}

	const handleSendTest = async () => {
		if (!editorValid) return
		setSendingTest(true)
		setError('')
		setTestSendMessage('')
		try {
			const result = await sendBlastTest(
				orgSlug,
				experienceSlug,
				buildPayload(),
			)
			const sentTargets = []
			if (result?.emailTo) sentTargets.push(`email to ${result.emailTo}`)
			if (result?.smsTo) sentTargets.push(`SMS to ${result.smsTo}`)
			setTestSendMessage(
				sentTargets.length
					? `Test sent: ${sentTargets.join(' and ')}.`
					: 'Test sent successfully.',
			)
		} catch (err) {
			setError(err.message || 'Failed to send test blast.')
		} finally {
			setSendingTest(false)
		}
	}

	const handleSendNow = async () => {
		if (submitting || !creationValid || selectedTravelerCount === 0) return
		setSubmitting(true)
		setError('')
		try {
			const blast = await upsertBlast()
			await sendBlast(orgSlug, experienceSlug, blast.id)
			navigate(
				`/${orgSlug}/experiences/${experienceSlug}/messaging/blasts/${blast.id}`,
			)
		} catch (err) {
			setError(err.message || 'Failed to send blast.')
		} finally {
			setSubmitting(false)
			setSendModalMode(null)
		}
	}

	const handleConfirmSchedule = async () => {
		if (submitting || !creationValid || selectedTravelerCount === 0) return
		const scheduledAt = toUtcIsoInTimezone(
			scheduleLocalValue,
			scheduledTimezone,
		)
		const validationError = getBlastScheduleError(scheduledAt)
		setScheduleError(validationError)
		if (validationError) return
		setSubmitting(true)
		setError('')
		try {
			const blast = await upsertBlast()
			await sendBlast(orgSlug, experienceSlug, blast.id, {
				scheduledAt,
				scheduledTimezone,
			})
			navigate(
				`/${orgSlug}/experiences/${experienceSlug}/messaging/blasts/${blast.id}`,
			)
		} catch (err) {
			setError(err.message || 'Failed to schedule blast.')
		} finally {
			setSubmitting(false)
			setSendModalMode(null)
		}
	}

	const emailEditorFields = (
		<>
			<VariableTriggerTextarea
				inputType='text'
				label='Subject'
				required={newBlastSubjectRequired}
				withAsterisk={newBlastSubjectRequired}
				value={emailSubject}
				onChange={setEmailSubject}
				placeholder={selectedTemplate?.defaultSubject || 'Subject'}
				variableOptions={availableVariableOptions}
			/>

			<FileInput
				label={`Header Image (${HEADER_IMAGE_TARGET_WIDTH}px by ${HEADER_IMAGE_TARGET_HEIGHT}px)`}
				placeholder='Choose header image'
				accept='image/png,image/jpeg,image/webp'
				onChange={handleHeaderImageSelect}
				description='Cropped to this standard banner size for consistent email layouts.'
			/>
			{headerImageUrl && (
				<Box>
					<Text size='xs' c='dimmed' mb={6}>
						Current Header Image (click to re-crop)
					</Text>
					<img
						src={headerImageUrl}
						alt='Header'
						onClick={handleOpenHeaderImageRecrop}
						style={{
							width: '100%',
							maxWidth: 560,
							borderRadius: 8,
							display: 'block',
							cursor: 'pointer',
						}}
					/>
					<Button
						mt='xs'
						size='xs'
						variant='subtle'
						color='red'
						onClick={() => setHeaderImageUrl('')}
					>
						Remove Header Image
					</Button>
				</Box>
			)}

			<VariableTriggerTextarea
				inputType='text'
				label='Header Text'
				value={headerText}
				onChange={setHeaderText}
				placeholder={`Welcome to ${previewContext.experienceName}`}
				variableOptions={availableVariableOptions}
			/>

			<ColorInput
				label='Separator Color'
				value={separatorColor}
				onChange={setSeparatorColor}
				format='hex'
			/>

			<VariableTriggerTextarea
				label='Email Body'
				required
				withAsterisk
				value={emailBody}
				onChange={setEmailBody}
				minRows={8}
				description={`Use ${EMAIL_SEPARATOR_TOKEN} on its own line to insert a colored separator.`}
				placeholder='Write your message body...'
				styles={{ input: { fontFamily: 'monospace' } }}
				variableOptions={availableVariableOptions}
			/>

			<Group spacing='xs'>
				<Button
					size='xs'
					variant='outline'
					onClick={handleInsertSeparatorInEmail}
				>
					Insert Separator
				</Button>
			</Group>

			{hasClickText && (
				<TextInput
					label='Pass Link Text'
					value={clickText}
					onChange={(e) => setClickText(e.currentTarget.value || 'CLICK HERE')}
					placeholder='CLICK HERE'
				/>
			)}
		</>
	)

	const smsEditorFields = (
		<>
			{channel === 'SMS' && (
				<VariableTriggerTextarea
					inputType='text'
					label='Internal Subject'
					required={newBlastSubjectRequired}
					withAsterisk={newBlastSubjectRequired}
					value={emailSubject}
					onChange={setEmailSubject}
					placeholder='Subject'
					description='Not sent in SMS messages.'
					variableOptions={availableVariableOptions}
				/>
			)}
			<VariableTriggerTextarea
				label='SMS Body'
				required
				withAsterisk
				value={smsBody}
				onChange={setSmsBody}
				minRows={4}
				placeholder='Write your SMS message...'
				description={buildBodyDescription(
					channel,
					renderedSmsBody,
					smsMediaUrl,
				)}
				styles={{ input: { fontFamily: 'monospace' } }}
				variableOptions={availableVariableOptions}
			/>
			<FileInput
				label='MMS Image'
				placeholder='Choose image'
				accept='image/png,image/jpeg,image/webp'
				onChange={handleSmsMediaSelect}
				disabled={uploadingSmsMedia}
				description='JPG, PNG, or WebP up to 1MB. Uploaded at original dimensions; not cropped.'
			/>
			{uploadingSmsMedia && (
				<Text size='sm' c='dimmed'>
					Uploading image...
				</Text>
			)}
			{smsMediaUrl && (
				<Box>
					<Text size='xs' c='dimmed' mb={6}>
						Current MMS Image
					</Text>
					<img
						src={smsMediaUrl}
						alt='MMS'
						style={{
							width: '100%',
							maxWidth: 360,
							borderRadius: 8,
							display: 'block',
						}}
					/>
					<Button
						mt='xs'
						size='xs'
						variant='subtle'
						color='red'
						onClick={() => setSmsMediaUrl('')}
					>
						Remove MMS Image
					</Button>
				</Box>
			)}
		</>
	)

	const emailPreview = (
		<Box>
			<Title order={5} mb='xs'>
				Email Preview
			</Title>
			<Box
				p='md'
				style={{
					border: '1px solid #ddd',
					borderRadius: '8px',
					backgroundColor: '#f9f9f9',
				}}
			>
				<Text fw={500}>To: {previewContext.to}</Text>
				<Text fw={500} mt={4}>
					Subject: {renderedSubject}
				</Text>
				<Box
					mt='xs'
					style={{
						borderRadius: '6px',
						overflow: 'hidden',
						border: '1px solid #d2d6dc',
						backgroundColor: '#ffffff',
					}}
					dangerouslySetInnerHTML={{ __html: renderedEmailHtml }}
				/>
			</Box>
		</Box>
	)

	const smsPreview = (
		<Box>
			<Title order={5} mb='xs'>
				Message Preview
			</Title>
			<Box
				p='md'
				style={{
					border: '1px solid #ddd',
					borderRadius: '8px',
					backgroundColor: '#f9f9f9',
					whiteSpace: 'pre-wrap',
				}}
			>
				<Text fw={500}>To: {previewContext.smsTo}</Text>
				<Text mt='xs'>{renderedSmsBody || '(empty message)'}</Text>
				{smsMediaUrl && (
					<Box mt='sm'>
						<img
							src={smsMediaUrl}
							alt='MMS preview'
							style={{
								width: '100%',
								maxWidth: 320,
								borderRadius: 8,
								display: 'block',
							}}
						/>
					</Box>
				)}
			</Box>
		</Box>
	)

	if (loadingBlast) return <Loader mt='xl' />

	return (
		<Stack spacing='md'>
			<Group position='apart'>
				<Group spacing='xs'>
					<Title order={2}>
						{isEditingBlast ? 'Edit Blast' : 'Create Blast'}
					</Title>
					{editingScheduledBlast && (
						<Badge color='blue' variant='light'>
							Editing Scheduled Blast
						</Badge>
					)}
				</Group>
				<Badge variant='light'>Timezone: {scheduledTimezone}</Badge>
			</Group>

			{error && <Text c='red'>{error}</Text>}
			{testSendMessage && <Text c='green'>{testSendMessage}</Text>}

			<Stepper
				active={activeStep}
				onStepClick={setActiveStep}
				allowNextStepsSelect={false}
			>
				<Stepper.Step label='Template & Channel'>
					<Stack spacing='md' mt='md'>
						<Box maw={360}>
							<Text size='sm' fw={500} mb={6}>
								Delivery Method
							</Text>
							<SegmentedControl
								value={channel}
								onChange={setChannel}
								disabled={isTemplateSource}
								data={[
									{ label: 'Email', value: 'EMAIL' },
									{ label: 'SMS', value: 'SMS' },
									{ label: 'Both', value: 'BOTH' },
								]}
							/>
							{isTemplateSource && (
								<Text size='xs' c='dimmed' mt={6}>
									On-demand templates use their configured delivery method.
								</Text>
							)}
						</Box>

						<SimpleGrid cols={2} breakpoints={[{ maxWidth: 'sm', cols: 1 }]}>
							<Card
								withBorder
								shadow='sm'
								radius='md'
								style={{
									borderColor: source === 'scratch' ? '#228be6' : undefined,
								}}
							>
								<Group position='apart' mb='xs'>
									<Text fw={600}>Start from Scratch</Text>
									<Badge variant='light'>Custom</Badge>
								</Group>
								<Text size='sm' c='dimmed' mb='sm'>
									Compose a brand-new blast without prefilled template content.
								</Text>
								<Button variant='light' onClick={handleChooseScratch}>
									Start from Scratch
								</Button>
							</Card>
						</SimpleGrid>

						<Group spacing='sm' align='center' noWrap my={2}>
							<Text size='sm' c='dimmed' fw={500}>
								Or select from a template
							</Text>
							<Box
								style={{
									flex: 1,
									height: 1,
									backgroundColor: 'var(--mantine-color-gray-3)',
								}}
							/>
						</Group>

						{loadingTemplates ? (
							<Loader size='sm' />
						) : (
							<>
								<SimpleGrid
									cols={2}
									breakpoints={[{ maxWidth: 'sm', cols: 1 }]}
								>
									{availableOnDemandTemplates.map((template) => {
										const setup = templateSetupMap[template.key]
										const configured = isTemplateConfigured(
											template,
											setup?.data,
											setup,
										)
										return (
											<Card
												key={template.key}
												withBorder
												shadow='sm'
												radius='md'
												style={{
													borderColor:
														source === 'template' &&
														templateKey === template.key
															? '#228be6'
															: undefined,
												}}
											>
												<Group position='apart' mb='xs'>
													<Text fw={600}>{template.name}</Text>
													<Badge
														color={configured ? 'green' : 'gray'}
														variant='dot'
													>
														{configured ? 'Configured' : 'Not configured'}
													</Badge>
												</Group>
												<Text size='sm' c='dimmed' mb='sm'>
													{template.description}
												</Text>
												<Button
													variant='light'
													onClick={() => handleChooseTemplate(template.key)}
												>
													Use Template
												</Button>
											</Card>
										)
									})}
								</SimpleGrid>
								{availableOnDemandTemplates.length === 0 && (
									<Text size='sm' c='dimmed'>
										No templates are available for the selected delivery method.
									</Text>
								)}
							</>
						)}
					</Stack>
				</Stepper.Step>

				<Stepper.Step label='Editor'>
					<Stack spacing='md' mt='md'>
						{isTemplateSource && selectedTemplate && (
							<Card withBorder radius='md' p='sm'>
								<Text fw={600}>Using Template: {selectedTemplate.name}</Text>
								<Text size='sm' c='dimmed'>
									{selectedTemplate.description}
								</Text>
							</Card>
						)}

						{showTabbedEditors ? (
							<Tabs
								value={activeEditorTab}
								onTabChange={(value) => setActiveEditorTab(value || 'email')}
							>
								<Tabs.List grow>
									<Tabs.Tab value='email'>Email</Tabs.Tab>
									<Tabs.Tab value='sms'>SMS</Tabs.Tab>
								</Tabs.List>

								<Tabs.Panel value='email' pt='md'>
									<Stack spacing='md'>
										{emailEditorFields}
										{emailPreview}
									</Stack>
								</Tabs.Panel>

								<Tabs.Panel value='sms' pt='md'>
									<Stack spacing='md'>
										{smsEditorFields}
										{smsPreview}
									</Stack>
								</Tabs.Panel>
							</Tabs>
						) : (
							<>
								{isEmailEnabled && emailEditorFields}
								{isSmsEnabled && smsEditorFields}

								{isEmailEnabled && emailPreview}
								{isSmsEnabled && smsPreview}
							</>
						)}

						<Group>
							<Button variant='default' onClick={() => setActiveStep(0)}>
								Back
							</Button>
							<Button
								variant='outline'
								onClick={handleSendTest}
								disabled={!creationValid || submitting || sendingTest}
								loading={sendingTest}
							>
								Send Test
							</Button>
							<Button onClick={() => setActiveStep(2)} disabled={!creationValid}>
								Continue to Recipients
							</Button>
						</Group>
					</Stack>
				</Stepper.Step>

				<Stepper.Step label='Recipients'>
					<Stack spacing='md' mt='md'>
						{loadingRecipients ? (
							<Loader />
						) : (
							<>
								<CheckInFilterBlock
									checkIns={checkIns}
									searchTerm={searchTerm}
									onSearchTermChange={setSearchTerm}
									checkInStatus={checkInStatusFilter}
									onCheckInStatusChange={setCheckInStatusFilter}
									stationFilter={stationFilter}
									onStationFilterChange={setStationFilter}
									stationOptions={stationOptions.map((station) => ({
										value: station.id,
										label: station.name,
									}))}
									itemFilter={itemFilter}
									onItemFilterChange={setItemFilter}
									itemOptions={itemOptions.map((item) => ({
										value: item.id,
										label: item.name,
									}))}
									filters={customFieldFilters}
									onFiltersChange={setCustomFieldFilters}
									marginBottom={0}
								/>

								<Card withBorder radius='md' p='sm'>
									<Group position='apart' align='center' noWrap>
										<Text fw={600}>
											Selected: {selectedTravelerCount} travelers (
											{selectedContactCount} contact points)
										</Text>
										{shouldShowToggleAllFilteredLink && (
											<Anchor
												size='sm'
												href='#'
												onClick={(event) => {
													event.preventDefault()
													handleToggleAllFiltered()
												}}
											>
												{toggleAllFilteredLinkLabel}
											</Anchor>
										)}
									</Group>
								</Card>

								<Table striped highlightOnHover>
									<thead>
										<tr>
											<th>
												<Checkbox
													checked={allVisibleSelected}
													onChange={toggleAllVisible}
												/>
											</th>
											<th>Traveler</th>
											<th>Email</th>
											<th>Phone</th>
											<th>Station</th>
											<th>Checked In</th>
										</tr>
									</thead>
									<tbody>
										{filteredCheckIns.length === 0 ? (
											<tr>
												<td colSpan={6}>
													<Text align='center' c='dimmed'>
														No matching travelers.
													</Text>
												</td>
											</tr>
										) : (
											paginatedFilteredCheckIns.map((row) => (
												<tr key={row.id}>
													<td>
														<Checkbox
															checked={selectedSet.has(row.id)}
															onChange={() => toggleCheckIn(row.id)}
														/>
													</td>
													<td>{row.travelerName}</td>
													<td>{row.travelerEmail || '—'}</td>
													<td>{row.travelerPhone || '—'}</td>
													<td>{row.stationName || '—'}</td>
													<td>{row.checkInStatus ? 'Yes' : 'No'}</td>
												</tr>
											))
										)}
									</tbody>
								</Table>
								<Box mt='sm'>
									<Text
										size='sm'
										c='dimmed'
										mb={recipientTotalPages > 1 ? 'xs' : 0}
									>
										{recipientCountLabel}
									</Text>
									{recipientTotalPages > 1 && (
										<Pagination
											value={recipientsPage}
											onChange={setRecipientsPage}
											total={recipientTotalPages}
											size='sm'
										/>
									)}
								</Box>
							</>
						)}

						<Group>
							<Button variant='default' onClick={() => setActiveStep(1)}>
								Back
							</Button>
							<Button
								variant='light'
								onClick={handleSaveDraft}
								disabled={!creationValid || submitting}
								loading={submitting}
							>
								{isEditingBlast
									? 'Save Changes as Draft'
									: 'Save Blast as Draft'}
							</Button>
							<Menu withinPortal position='bottom-start'>
								<Menu.Target>
									<Button
										disabled={
											!creationValid || !selectedTravelerCount || submitting
										}
										loading={submitting}
										rightSection='▾'
									>
										Send Blast
									</Button>
								</Menu.Target>
								<Menu.Dropdown>
									<Menu.Item
										onClick={() => setSendModalMode('now')}
										disabled={submitting}
									>
										Send Now to {selectedTravelerCount} travelers (
										{selectedContactCount} contacts)
									</Menu.Item>
									<Menu.Item
										onClick={() => setSendModalMode('schedule')}
										disabled={submitting}
									>
										Send Later to {selectedTravelerCount} travelers (
										{selectedContactCount} contacts)
									</Menu.Item>
								</Menu.Dropdown>
							</Menu>
						</Group>
					</Stack>
				</Stepper.Step>
			</Stepper>

			<BlastSendModal
				mode={sendModalMode}
				onClose={() => setSendModalMode(null)}
				onConfirm={sendModalMode === 'now' ? handleSendNow : handleConfirmSchedule}
				travelerCount={selectedTravelerCount}
				submitting={submitting}
				confirmDisabled={!creationValid || !selectedTravelerCount}
				scheduledTimezone={scheduledTimezone}
				scheduleLocalValue={scheduleLocalValue}
				scheduleError={scheduleError}
				onScheduleChange={(value) => {
					setScheduleLocalValue(value)
					setScheduleError('')
				}}
			/>

			<ImageCropperModal
				opened={cropModalOpened}
				onClose={() => setCropModalOpened(false)}
				title='Crop Header Image'
				sourceUrl={cropSourceUrl}
				targetWidth={HEADER_IMAGE_TARGET_WIDTH}
				targetHeight={HEADER_IMAGE_TARGET_HEIGHT}
				onApply={handleUploadCroppedHeaderImage}
				applyLabel='Use Cropped Header'
				processing={uploadingHeaderImage}
			/>
		</Stack>
	)
}
